import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { CancelRequestOptions, ExtraFieldsParam, ContentPageServices, GetContentPagesParams } from "@/services/bedrock";
import { useCallback, useMemo } from "react";

function contentPageExtraFieldsQueryKey(extraFields: ExtraFieldsParam | undefined): unknown {
  if (extraFields === undefined) return [];
  if (extraFields === true) return true;
  if (Array.isArray(extraFields)) return extraFields.slice().sort();
  const record = extraFields as Exclude<ExtraFieldsParam, true | readonly string[]>;
  return Object.keys(record)
    .sort()
    .map(key => [key, contentPageExtraFieldsQueryKey(record[key])] as const);
}

function buildContentPagesQueryOptions(
  coachId: number | undefined,
  params: GetContentPagesParams = {},
  options?: CancelRequestOptions
) {
  const segmentationKey = Array.isArray(params.segmentationId)
    ? params.segmentationId.slice().sort()
    : params.segmentationId ?? null;
  const extraFieldsKey = contentPageExtraFieldsQueryKey(params.extraFields);
  return {
    queryKey: [
      "coachContentPagesList",
      coachId,
      params.search,
      params.myPages,
      params.playerId,
      params.superPageId,
      segmentationKey,
      extraFieldsKey,
      params.page,
      params.perPage,
      params.sort,
      params.order,
    ] as const,
    queryFn: () => ContentPageServices.getContentPages(params, options),
    staleTime: 1000 * 60 * 5, // 5 minutes
  };
}

export const useContentPageSearch = () => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const searchContentPages = useCallback(
    (params: GetContentPagesParams = {}) => {
      const options = buildContentPagesQueryOptions(coachId, params);
      const {
        data: contentPages,
        isFetching,
        isError,
      } = useQuery({
        ...options,
        placeholderData: keepPreviousData,
      });
      return { contentPages, isFetching, isError };
    },
    [coachId, queryClient]
  );

  const fetchContentPages = useCallback(
    (params: GetContentPagesParams = {}, options?: CancelRequestOptions) =>
      queryClient.fetchQuery(buildContentPagesQueryOptions(coachId, params, options)),
    [coachId, queryClient]
  );

  const clearContentPagesSearchCache = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["coachContentPagesList", coachId] }).catch(console.error),
    [coachId, queryClient]
  );

  return useMemo(
    () => ({
      searchContentPages,
      fetchContentPages,
      clearContentPagesSearchCache,
    }),
    [searchContentPages, fetchContentPages, clearContentPagesSearchCache]
  );
};
